import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/apiConfig';
import { Button, Alert, Spinner, Badge } from './ui';
import { SparklesIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

interface Article {
  id: number;
  title: string;
  url: string;
  content: string;
  author?: string;
  published_date?: string;
  summary?: string;
  created_at: string;
  updated_at: string;
}

const ArticleList: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [newArticleUrl, setNewArticleUrl] = useState('');
  const [fetchingArticle, setFetchingArticle] = useState(false);
  const [summarizing, setSummarizing] = useState<{ [key: number]: boolean }>({});
  const [deleting, setDeleting] = useState<{ [key: number]: boolean }>({});
  const [hoveredArticleId, setHoveredArticleId] = useState<number | null>(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/articles/');
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setError('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFromUrl = async () => {
    if (!newArticleUrl.trim()) {
      alert('Please enter an article URL');
      return;
    }

    setFetchingArticle(true);
    try {
      const response = await apiClient.post('/api/articles/fetch_from_url/', {
        url: newArticleUrl
      });

      setArticles([response.data.article, ...articles]);
      setNewArticleUrl('');
      alert('Article imported successfully!');
    } catch (error: any) {
      console.error('Error fetching article:', error);
      alert(error.response?.data?.error || 'Failed to fetch article from URL');
    } finally {
      setFetchingArticle(false);
    }
  };

  const handleGenerateSummary = async (articleId: number) => {
    setSummarizing(prev => ({ ...prev, [articleId]: true }));
    try {
      const response = await apiClient.post(`/api/articles/${articleId}/generate_summary/`, {
        model: 'gpt-4o-mini'
      });

      setArticles(prevArticles => prevArticles.map(article => {
        if (article.id === articleId) {
          return { ...article, summary: response.data.summary };
        }
        return article;
      }));
    } catch (error: any) {
      console.error('Error generating summary:', error);
      alert(error.response?.data?.error || 'Failed to generate summary');
    } finally {
      setSummarizing(prev => ({ ...prev, [articleId]: false }));
    }
  };

  const handleDelete = async (articleId: number, articleTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${articleTitle}"?`)) {
      return;
    }

    setDeleting(prev => ({ ...prev, [articleId]: true }));
    try {
      await apiClient.delete(`/api/articles/${articleId}/`);
      setArticles(prevArticles => prevArticles.filter(article => article.id !== articleId));
    } catch (error: any) {
      console.error('Error deleting article:', error);
      alert(error.response?.data?.error || 'Failed to delete article');
    } finally {
      setDeleting(prev => ({ ...prev, [articleId]: false }));
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '128px',
      }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        {error}
      </Alert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {/* URL Import Section */}
      <div style={{
        backgroundColor: COLORS.bg.panel,
        borderRadius: RADIUS,
        padding: SPACING[16],
        border: `1px solid ${COLORS.border.default}`,
      }}>
        <h3 style={{
          fontSize: TYPOGRAPHY.size.sm,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary,
          marginBottom: SPACING[12],
        }}>Import Article from URL</h3>
        <div style={{ display: 'flex', gap: SPACING[8] }}>
          <input
            type="text"
            value={newArticleUrl}
            onChange={(e) => setNewArticleUrl(e.target.value)}
            placeholder="Paste article URL here..."
            style={{
              flex: 1,
              padding: `${SPACING[8]} ${SPACING[12]}`,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.size.base,
              fontFamily: TYPOGRAPHY.body.fontFamily,
              outline: 'none',
            }}
          />
          <Button
            onClick={handleFetchFromUrl}
            disabled={fetchingArticle}
            variant="primary"
            size="md"
          >
            {fetchingArticle ? (
              <>
                <Spinner size="sm" style={{ marginRight: SPACING[8] }} />
                Fetching...
              </>
            ) : (
              <>
                <PlusIcon style={{ height: '16px', width: '16px', marginRight: SPACING[8] }} />
                Import Article
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Article List */}
      <div style={{
        backgroundColor: COLORS.bg.panel,
        borderRadius: RADIUS,
        border: `1px solid ${COLORS.border.default}`,
      }}>
        {articles.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: `${SPACING[48]} ${SPACING[16]}`,
          }}>
            <p style={{ color: COLORS.text.secondary }}>No articles yet. Import one from a URL above!</p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {articles.map((article, index) => (
              <li
                key={article.id}
                style={{
                  padding: SPACING[16],
                  backgroundColor: hoveredArticleId === article.id ? COLORS.bg.hover : 'transparent',
                  borderBottom: index < articles.length - 1 ? `1px solid ${COLORS.border.default}` : 'none',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={() => setHoveredArticleId(article.id)}
                onMouseLeave={() => setHoveredArticleId(null)}
              >
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleExpanded(article.id)}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{
                        fontSize: TYPOGRAPHY.size.lg,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.text.primary,
                      }}>{article.title}</h3>
                      {article.author && (
                        <p style={{
                          marginTop: SPACING[4],
                          fontSize: TYPOGRAPHY.size.sm,
                          color: COLORS.text.secondary,
                        }}>By {article.author}</p>
                      )}
                      <div style={{
                        marginTop: SPACING[8],
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACING[8],
                        fontSize: TYPOGRAPHY.size.sm,
                        color: COLORS.text.secondary,
                      }}>
                        {article.content && (
                          <Badge variant="info" size="sm">
                            Has Content
                          </Badge>
                        )}
                        {article.summary && (
                          <Badge variant="success" size="sm">
                            Has Summary
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div style={{
                      marginLeft: SPACING[16],
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING[8],
                    }}>
                      {article.url && (
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="primary" size="md">
                            Read Article
                          </Button>
                        </a>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(article.id, article.title);
                        }}
                        disabled={deleting[article.id]}
                        style={{
                          padding: SPACING[8],
                          color: COLORS.status.error,
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: RADIUS,
                          cursor: deleting[article.id] ? 'not-allowed' : 'pointer',
                          opacity: deleting[article.id] ? 0.5 : 1,
                          transition: 'background-color 0.15s ease, color 0.15s ease',
                        }}
                        title="Delete article"
                      >
                        {deleting[article.id] ? (
                          <Spinner size="sm" />
                        ) : (
                          <TrashIcon style={{ height: '20px', width: '20px' }} />
                        )}
                      </button>
                      <button style={{
                        color: COLORS.text.tertiary,
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: SPACING[4],
                      }}>
                        {expandedId === article.id ? (
                          <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        ) : (
                          <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedId === article.id && (
                  <div style={{
                    marginTop: SPACING[16],
                    display: 'flex',
                    flexDirection: 'column',
                    gap: SPACING[16],
                  }}>
                    {article.content ? (
                      <>
                        {/* AI Summary Section */}
                        <div style={{
                          backgroundColor: '#FAF5FF',
                          border: '1px solid #E9D5FF',
                          borderRadius: RADIUS,
                          padding: SPACING[12],
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: SPACING[8],
                          }}>
                            <h4 style={{
                              fontSize: TYPOGRAPHY.size.sm,
                              fontWeight: TYPOGRAPHY.weight.medium,
                              color: '#581C87',
                              display: 'flex',
                              alignItems: 'center',
                            }}>
                              <SparklesIcon style={{ height: '16px', width: '16px', marginRight: SPACING[4] }} />
                              AI Summary
                            </h4>
                            {!article.summary && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGenerateSummary(article.id);
                                }}
                                disabled={summarizing[article.id]}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: `${SPACING[4]} ${SPACING[8]}`,
                                  fontSize: TYPOGRAPHY.size.xs,
                                  fontWeight: TYPOGRAPHY.weight.medium,
                                  borderRadius: RADIUS,
                                  color: '#6B21A8',
                                  backgroundColor: '#F3E8FF',
                                  border: 'none',
                                  cursor: summarizing[article.id] ? 'not-allowed' : 'pointer',
                                  opacity: summarizing[article.id] ? 0.5 : 1,
                                }}
                              >
                                <SparklesIcon style={{ height: '12px', width: '12px', marginRight: SPACING[4] }} />
                                {summarizing[article.id] ? 'Generating...' : 'Generate Summary'}
                              </button>
                            )}
                          </div>
                          {article.summary ? (
                            <div style={{
                              fontSize: TYPOGRAPHY.size.sm,
                              color: COLORS.text.primary,
                              whiteSpace: 'pre-wrap',
                            }}>
                              {article.summary}
                            </div>
                          ) : (
                            <p style={{
                              fontSize: TYPOGRAPHY.size.xs,
                              color: COLORS.text.secondary,
                              fontStyle: 'italic',
                            }}>
                              No summary yet. Click "Generate Summary" to create an AI-powered summary.
                            </p>
                          )}
                        </div>

                        {/* Article Content Section */}
                        <div style={{
                          backgroundColor: COLORS.bg.hover,
                          border: `1px solid ${COLORS.border.default}`,
                          borderRadius: RADIUS,
                          padding: SPACING[12],
                        }}>
                          <h4 style={{
                            fontSize: TYPOGRAPHY.size.sm,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.primary,
                            marginBottom: SPACING[8],
                          }}>Full Content</h4>
                          <div style={{
                            fontSize: TYPOGRAPHY.size.sm,
                            color: COLORS.text.secondary,
                            maxHeight: '256px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                          }}>
                            {article.content}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{
                        backgroundColor: '#FFFBEB',
                        border: '1px solid #FDE68A',
                        borderRadius: RADIUS,
                        padding: SPACING[16],
                      }}>
                        <p style={{
                          fontSize: TYPOGRAPHY.size.sm,
                          color: '#92400E',
                        }}>
                          No content was extracted from this article. This might happen if the website blocks automated content extraction.
                          You can still visit the article using the "Read Article" button above.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ArticleList;
